interface HeroProps {
  onBookDemo: () => void;
  onSeeHowItWorks: () => void;
}

export function Hero({ onBookDemo, onSeeHowItWorks }: HeroProps) {
  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden pt-20">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-secondary via-secondary/95 to-primary/20">
        <img
          src="/readdy-assets/hero-bg-002.jpg"
          alt="Technology Background"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/40 via-secondary/30 to-secondary/40"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-white space-y-8">
            <div className="space-y-5 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <h1 className="font-display text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.1] tracking-tight">
                Never Miss Another 
                <span className="block text-primary mt-2">Customer Call Again.</span>
              </h1>
              <p className="text-lg lg:text-xl text-gray-300 font-light leading-relaxed max-w-xl">
                We answer every call, follow up with leads automatically, and book appointments so you can focus on running your business instead of chasing voicemails.
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <button
                onClick={onBookDemo}
                className="group bg-white hover:bg-gray-50 text-secondary px-8 py-4 rounded-full font-semibold text-base lg:text-lg transition-all duration-200 hover:scale-105 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer shadow-lg"
              >
                Book a Free Demo
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
                </div>
              </button>
              <button
                onClick={onSeeHowItWorks}
                className="group border-2 border-white/30 hover:border-white/60 text-white px-8 py-4 rounded-full font-semibold text-base lg:text-lg transition-all duration-200 hover:bg-white/10 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-play-circle-line text-lg"></i>
                </div>
                See How It Works
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-6 pt-2 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              {[
                { icon: 'ri-phone-line', label: '24/7 Call Answering' },
                { icon: 'ri-user-follow-line', label: 'Instant Lead Follow-Up' },
                { icon: 'ri-calendar-check-line', label: 'Auto Appointment Booking' },
              ].map(({ icon, label }) => (
                <div key={label} className="flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${icon} text-primary text-lg`}></i>
                  </div>
                  <span className="text-sm text-gray-300 font-medium">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Visual */}
          <div className="hidden lg:block relative animate-fade-in-right" style={{ animationDelay: '0.2s' }}>
            <div className="relative bg-white rounded-2xl p-4 shadow-2xl w-full h-[520px] flex items-center justify-center overflow-hidden">
              <img
                src="/images/hero-ai-phone.png"
                alt="AI Phone Answering Service hero illustration"
                className="w-full h-full object-cover rounded-xl hover:scale-105 transition-transform duration-500"
                loading="eager"
              />
              {/* Floating stat card */}
              <div className="absolute -bottom-3 -left-3 bg-white text-secondary px-5 py-4 rounded-xl shadow-2xl animate-bounce-subtle border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-full">
                    <i className="ri-customer-service-line text-primary text-xl"></i>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">24/7</div>
                    <div className="text-xs text-gray-500 font-medium">Always On</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-10 h-10 flex items-center justify-center">
          <i className="ri-arrow-down-line text-white text-3xl"></i>
        </div>
      </div>
    </section>
  );
}